import { useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { App, Button, Card, Col, Form, Input, Row, Select, Skeleton, Space, Table, Tag, Typography } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, ExclamationCircleFilled, ExportOutlined, QuestionCircleFilled, SafetyCertificateOutlined, SendOutlined } from '@ant-design/icons';
import { healthSummary, type DomainHealthCheck, type DomainHealthResult, type HealthGroup, type HealthStatus } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { CardTitle } from '../../components/StatCard';
import { EmptyState, LoadError } from '../../components/states';
import { errorMessage } from '../../api/client';
import { useAgents } from '../../features/email/api';
import { useDomainHealth, useDomainTestEmail, useRunDomainHealth, useWarmupDomains } from '../../features/warmup/api';
import { fmtDateTime } from '../../utils/format';
import { palette } from '../../app/theme';

const STATUS_ICON: Record<HealthStatus, ReactNode> = {
  pass: <CheckCircleFilled style={{ color: palette.success }} />,
  warn: <ExclamationCircleFilled style={{ color: palette.warning }} />,
  fail: <CloseCircleFilled style={{ color: palette.error }} />,
  unknown: <QuestionCircleFilled style={{ color: palette.textTertiary }} />,
};

const GROUPS: { key: HealthGroup; title: string; subtitle: string }[] = [
  { key: 'authentication', title: 'Authentication', subtitle: 'Proves to inbox providers that your email really comes from you. Gmail and Yahoo require SPF, DKIM and DMARC.' },
  { key: 'receiving', title: 'Receiving', subtitle: 'Replies, including warm-up partner replies, reach the CRM.' },
  { key: 'reputation', title: 'Reputation', subtitle: 'Blocklists that many providers consult, and things that make mail look like bulk mail.' },
  { key: 'signals', title: 'Bounces and complaints', subtitle: 'What recipients and their providers report back.' },
];

const RECORD_STATUS: Record<string, string> = { verified: 'success', pending: 'warning', not_started: 'default', failed: 'error', temporary_failure: 'warning' };

/** Tools → Domain health: DNS, blocklist and rate checks for a sending domain, plus the checks done outside the CRM. */
export function DomainHealthPage() {
  const domains = useWarmupDomains();
  const [params, setParams] = useSearchParams();
  const domainId = params.get('domain') ?? domains.data?.[0]?.id;
  const domain = domains.data?.find((d) => d.id === domainId);
  const health = useDomainHealth(domainId);
  const run = useRunDomainHealth();
  const { message } = App.useApp();
  const result = health.data;

  const check = () => domainId && run.mutateAsync(domainId).catch((e) => message.error(errorMessage(e)));
  // a domain never checked gets its first check on opening
  useEffect(() => {
    if (domainId && health.isSuccess && health.data === null && !run.isPending) void check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId, health.isSuccess, health.data]);

  return (
    <div className="page">
      <PageHeader
        title="Domain health"
        subtitle="Checks a sending domain's DNS records, blocklists and bounce rates, says what to fix, and lists the checks to run outside the CRM. Runs on its own every morning."
        extra={
          domains.data?.length ? (
            <Space wrap>
              <Select style={{ minWidth: 220 }} value={domainId} onChange={(v) => setParams({ domain: v })} options={domains.data.map((d) => ({ value: d.id, label: d.domain }))} aria-label="Domain" />
              <Button type="primary" icon={<SafetyCertificateOutlined />} loading={run.isPending} onClick={() => void check()}>Check now</Button>
            </Space>
          ) : null
        }
      />
      {domains.isError ? (
        <Card><LoadError error={domains.error} onRetry={domains.refetch} title="Could not load domains" /></Card>
      ) : !domains.data ? (
        <Card><Skeleton active /></Card>
      ) : !domains.data.length ? (
        <Card><EmptyState description="No sending domains yet." action={<Link to="/settings/domains"><Button type="primary">Add a domain</Button></Link>} /></Card>
      ) : (
        <Row gutter={[24, 24]}>
          <Col xs={24} xl={15}>
            {health.isError ? (
              <Card><LoadError error={health.error} onRetry={health.refetch} title="Could not load the last check" /></Card>
            ) : !result ? (
              <Card><Skeleton active paragraph={{ rows: 8 }} /></Card>
            ) : (
              <Results result={result} running={run.isPending} />
            )}
          </Col>
          <Col xs={24} xl={9}>
            <OutsideChecks domain={domain?.domain ?? ''} domainId={domainId} />
          </Col>
        </Row>
      )}
    </div>
  );
}

function Results({ result, running }: { result: DomainHealthResult; running: boolean }) {
  const sum = healthSummary(result.checks);
  return (
    <>
      <Card style={{ marginBottom: 24, opacity: running ? 0.6 : 1 }}>
        <Space size={[8, 8]} wrap align="center">
          <Typography.Text strong style={{ fontSize: 16 }}>{result.domain}</Typography.Text>
          {sum.fail > 0 && <Tag color="error">{sum.fail} to fix</Tag>}
          {sum.warn > 0 && <Tag color="warning">{sum.warn} to look at</Tag>}
          <Tag color="success">{sum.pass} passed</Tag>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>Checked {fmtDateTime(result.checkedAt)}</Typography.Text>
        </Space>
      </Card>
      {GROUPS.map((g) => {
        const checks = result.checks.filter((c) => c.group === g.key);
        if (!checks.length) return null;
        return (
          <Card key={g.key} style={{ marginBottom: 24, opacity: running ? 0.6 : 1 }} title={<CardTitle title={g.title} subtitle={g.subtitle} />} styles={{ body: { padding: 0 } }}>
            {checks.map((c, i) => <CheckRow key={c.key} check={c} first={i === 0} />)}
          </Card>
        );
      })}
      {result.resendRecords && (
        <Card title={<CardTitle title="Records Resend expects" subtitle="Add these at your DNS provider exactly as shown. Names are relative to your domain's zone." />}>
          <Table
            rowKey={(r) => `${r.record}-${r.type}-${r.name}`}
            size="small"
            pagination={false}
            scroll={{ x: 720 }}
            dataSource={result.resendRecords}
            columns={[
              { title: 'Purpose', dataIndex: 'record', width: 100 },
              { title: 'Type', dataIndex: 'type', width: 70 },
              { title: 'Name', dataIndex: 'name', width: 180, render: (v: string) => <Typography.Text code copyable>{v}</Typography.Text> },
              { title: 'Value', dataIndex: 'value', render: (v: string, r) => <Typography.Text code copyable={{ text: v }} style={{ wordBreak: 'break-all' }}>{v.length > 60 ? `${v.slice(0, 57)}…` : v}{r.priority !== undefined ? ` (priority ${r.priority})` : ''}</Typography.Text> },
              { title: 'Status', dataIndex: 'status', width: 110, render: (v: string) => <Tag color={RECORD_STATUS[v] ?? 'default'}>{v.replace('_', ' ')}</Tag> },
            ]}
          />
        </Card>
      )}
    </>
  );
}

function CheckRow({ check: c, first }: { check: DomainHealthCheck; first: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 14, padding: '16px 24px', borderTop: first ? undefined : `1px solid ${palette.border}` }}>
      <span style={{ fontSize: 18, lineHeight: '22px' }}>{STATUS_ICON[c.status]}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Typography.Text strong>{c.label}</Typography.Text>
        <Typography.Paragraph style={{ margin: '2px 0 0', fontSize: 13 }}>{c.detail}</Typography.Paragraph>
        {c.fix && (
          <Typography.Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 13 }}>
            <b>{c.status === 'pass' ? 'Tip' : 'Fix'}:</b> <Linkify text={c.fix} />
          </Typography.Paragraph>
        )}
        {c.record && (
          <div style={{ marginTop: 8, background: palette.canvas, border: `1px solid ${palette.border}`, borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
            <div><Typography.Text type="secondary">Type</Typography.Text> <Typography.Text code>{c.record.type}</Typography.Text></div>
            <div><Typography.Text type="secondary">Name</Typography.Text> <Typography.Text code copyable>{c.record.name}</Typography.Text></div>
            <div style={{ wordBreak: 'break-all' }}><Typography.Text type="secondary">Value</Typography.Text> <Typography.Text code copyable>{c.record.value}</Typography.Text></div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Turns the URLs in a fix into links. */
function Linkify({ text }: { text: string }) {
  const parts = text.split(/(https:\/\/\S+)/);
  return <>{parts.map((p, i) => (p.startsWith('https://') ? <a key={i} href={p} target="_blank" rel="noreferrer">{p}</a> : p))}</>;
}

function OutsideChecks({ domain, domainId }: { domain: string; domainId?: string }) {
  const items: { title: string; href: string; body: ReactNode }[] = [
    {
      title: 'Google Postmaster Tools',
      href: 'https://postmaster.google.com/',
      body: <>Add <b>{domain || 'your domain'}</b> (Google asks for one TXT record to prove it's yours). During warm-up, check its domain reputation and spam rate every few days: you want “High” reputation and a spam rate under 0.1%. Data only shows once you send a fair number of emails to Gmail each day.</>,
    },
    {
      title: 'mail-tester.com',
      href: 'https://www.mail-tester.com/',
      body: <>Open the site, copy the address it shows, and send a test to it with the form below. It scores the email out of 10 and lists what to fix. Aim for 9 or more.</>,
    },
    {
      title: 'Inbox placement',
      href: 'https://support.google.com/a/answer/81126',
      body: <>Add your own Gmail, Outlook and Yahoo addresses as warm-up partners and look where the emails land: Inbox, Promotions or Spam. Move any in Spam to the Inbox. The link is Google's sender guidelines.</>,
    },
    {
      title: 'MXToolbox',
      href: domain ? `https://mxtoolbox.com/SuperTool.aspx?action=blacklist%3a${encodeURIComponent(domain)}` : 'https://mxtoolbox.com/blacklists.aspx',
      body: <>A second opinion on blocklists and DNS, including lists this page can't query from the server.</>,
    },
    {
      title: 'Resend dashboard',
      href: 'https://resend.com/domains',
      body: <>Domain records and verification, plus Resend's own bounce and complaint numbers. Resend can also pause sending on accounts with high bounce or complaint rates.</>,
    },
  ];
  return (
    <>
      <Card style={{ marginBottom: 24 }} title={<CardTitle title="Checks outside the CRM" subtitle="Do these at the start of a warm-up, then weekly." />} styles={{ body: { padding: 0 } }}>
        {items.map((it, i) => (
          <div key={it.title} style={{ padding: '16px 24px', borderTop: i ? `1px solid ${palette.border}` : undefined }}>
            <a href={it.href} target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>
              {it.title} <ExportOutlined style={{ fontSize: 12 }} />
            </a>
            <Typography.Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 13 }}>{it.body}</Typography.Paragraph>
          </div>
        ))}
      </Card>
      <TestEmailCard domainId={domainId} />
    </>
  );
}

function TestEmailCard({ domainId }: { domainId?: string }) {
  const { message } = App.useApp();
  const agents = useAgents();
  const send = useDomainTestEmail();
  const [form] = Form.useForm<{ agentId: string; to: string }>();
  const options = (agents.data ?? []).filter((a) => a.isActive && a.domainId === domainId).map((a) => ({ value: a.id, label: `${a.name} <${a.email}>` }));
  const [lastTo, setLastTo] = useState<string | null>(null);
  useEffect(() => {
    form.setFieldsValue({ agentId: options[0]?.value });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId, agents.data]);
  return (
    <Card title={<CardTitle title="Send a test email" subtitle="A short, plain email like the warm-up ones, from an agent on this domain. Not counted as a warm-up email." />}>
      {!options.length ? (
        <Typography.Text type="secondary">No active agents on this domain. <Link to="/settings/agents">Add one</Link>.</Typography.Text>
      ) : (
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={(v) =>
            send
              .mutateAsync(v)
              .then((r) => {
                setLastTo(r.to);
                message.success(`Sent “${r.subject}” to ${r.to}`);
              })
              .catch((e) => message.error(errorMessage(e)))
          }
        >
          <Form.Item name="agentId" label="From" rules={[{ required: true }]}>
            <Select options={options} />
          </Form.Item>
          <Form.Item name="to" label="To" rules={[{ required: true, message: 'Enter an address' }, { type: 'email', message: 'Enter an email address' }]} extra="For example the address mail-tester.com shows you.">
            <Input placeholder="test-abc123@srv1.mail-tester.com" />
          </Form.Item>
          <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={send.isPending}>Send test</Button>
          {lastTo && <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 13 }}>Sent to {lastTo}. Give it a minute, then check the result there.</Typography.Text>}
        </Form>
      )}
    </Card>
  );
}
